import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { getAccessToken } from "../utils/auth";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4010";

// 순환 참조 방지를 위한 동적 authStore 설정
let authStore: { clearAuth: () => void } | null = null;

export const setAuthStore = (store: { clearAuth: () => void }) => {
  authStore = store;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`, // 백엔드가 /api prefix 사용
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

/**
 * 클라이언트 IP 주소를 가져오는 함수
 * - localStorage에 캐싱하여 매번 API 호출하지 않음
 * - 1시간마다 갱신
 */
let cachedIp: string | null = null;
let ipFetchPromise: Promise<string> | null = null;

const getClientIp = async (): Promise<string> => {
  // 이미 가져오는 중이면 동일한 Promise 반환 (중복 요청 방지)
  if (ipFetchPromise) {
    return ipFetchPromise;
  }

  // 캐시된 IP가 있고 아직 유효하면 반환
  const cachedData = localStorage.getItem("client_ip_cache");
  if (cachedData) {
    try {
      const { ip, timestamp } = JSON.parse(cachedData);
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - timestamp < oneHour) {
        cachedIp = ip;
        return ip;
      }
    } catch (error) {
      console.error("캐시된 IP 파싱 실패:", error);
    }
  }

  // 새로 IP 가져오기
  ipFetchPromise = (async () => {
    try {
      // ipify.org는 간단하고 빠른 무료 IP 조회 서비스
      const response = await axios.get("https://api.ipify.org?format=json", {
        timeout: 3000,
      });
      const ip = response.data.ip;

      // localStorage에 캐싱 (타임스탬프 포함)
      localStorage.setItem(
        "client_ip_cache",
        JSON.stringify({
          ip,
          timestamp: Date.now(),
        })
      );

      cachedIp = ip;
      return ip;
    } catch (error) {
      console.error("클라이언트 IP 조회 실패:", error);
      // 실패 시 기본값 반환
      return "unknown";
    } finally {
      ipFetchPromise = null;
    }
  })();

  return ipFetchPromise;
};

// 요청 인터셉터: 모든 요청에 IP 주소 및 accessToken 첨부
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // IP 주소를 커스텀 헤더에 추가
    // 백엔드에서 X-Client-IP 헤더를 읽어서 로그에 기록
    try {
      const clientIp = cachedIp || (await getClientIp());
      config.headers["X-Client-IP"] = clientIp;
    } catch (error) {
      console.error("IP 주소 추가 실패:", error);
      // IP 추가 실패해도 요청은 계속 진행
    }

    // 쿠키에서 accessToken을 읽어서 Authorization 헤더에 추가
    // accessToken을 쿠키에서 읽을 수 없어도 쿠키로 자동 전송됨 (withCredentials: true)
    // 백엔드가 쿠키에서 accessToken을 읽을 수 있음
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 401 에러 재시도 상태 관리
let isRetrying = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// 응답 인터셉터: 401 에러 시 백엔드 자동 갱신 후 재시도
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // 401 에러 처리 (백엔드가 자동으로 토큰 갱신 처리)
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log("🔐 401 에러 감지:", {
        url: originalRequest.url,
        method: originalRequest.method,
        isRetrying,
        timestamp: new Date().toISOString(),
      });

      // 이미 재시도 중이면 대기열에 추가
      if (isRetrying) {
        console.log("⏳ 이미 재시도 중, 대기열에 추가");
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            console.log("✅ 대기 중인 요청 재시도:", originalRequest.url);
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRetrying = true;

      try {
        // 백엔드가 자동으로 토큰 갱신 처리했을 것으로 가정하고 짧은 지연 후 재시도
        // 백엔드가 쿠키에 새로운 accessToken을 설정했을 수 있음
        await new Promise((resolve) => setTimeout(resolve, 100));

        console.log("🔄 원래 요청 재시도:", {
          url: originalRequest.url,
          method: originalRequest.method,
          timestamp: new Date().toISOString(),
        });

        // 원래 요청 재시도 (백엔드가 이미 토큰을 갱신했을 것으로 가정)
        const retryResponse = await apiClient(originalRequest);

        console.log("✅ 재시도 성공:", {
          url: originalRequest.url,
          status: retryResponse.status,
        });

        // 재시도 성공 시 대기 중인 모든 요청 처리
        processQueue(null);
        return retryResponse;
      } catch (retryError) {
        const retryAxiosError = retryError as AxiosError;

        // 재시도 후에도 401이면 refreshToken도 만료된 것으로 간주
        if (retryAxiosError.response?.status === 401) {
          console.error(
            "❌ 재시도 후에도 401 에러 - refreshToken도 만료됨, 로그아웃 처리"
          );
          processQueue(retryAxiosError);
        if (authStore) {
          authStore.clearAuth();
          }
        } else {
          // 다른 에러는 그대로 전달
          processQueue(retryAxiosError);
        }

        return Promise.reject(retryError);
      } finally {
        isRetrying = false;
      }
    }

    // 403 Forbidden 에러 처리: 권한 없음
    if (error.response?.status === 403) {
      const responseData = error.response.data as {
        statusCode?: number;
        message?: string;
        error?: string;
      } | undefined;

      // 백엔드에서 제공한 메시지가 있으면 사용, 없으면 기본 메시지
      const errorMessage =
        responseData?.message ||
        responseData?.error ||
        "이 작업을 수행할 권한이 없습니다.";

      // 에러 객체에 메시지 추가
      const forbiddenError = new Error(errorMessage) as Error & {
        response?: typeof error.response;
        isAxiosError?: boolean;
      };
      forbiddenError.response = error.response;
      forbiddenError.isAxiosError = true;

      console.error("🚫 403 권한 없음:", {
        url: error.config?.url,
        method: error.config?.method,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      });

      return Promise.reject(forbiddenError);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
